import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import styled, { css } from "styled-components";
import { LIMIT_PRODUCT_PER_PAGE } from "../constants";
import { COLOR_WHITE, COLOR_YELLOW } from "../constants/colors";
import { Subtitle1 } from "./Typography";

type PaginationProps = {
  currentPage: number;
  totalItems: number;
  onChangePage: (page: number) => void;
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 18px;
`;

const PaginationNumber = styled.div<{ selected: boolean }>`
  ${Subtitle1}
  color: ${COLOR_WHITE};
  cursor: pointer;
  padding: 0 12px;

  ${(props) =>
    props.selected &&
    css`
      font-weight: 400;
      color: ${COLOR_YELLOW};
      cursor: auto;
    `}
`;

const IcChevron = css`
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: ${COLOR_WHITE};
`;

const IcChevronLeft = styled(ChevronLeft)<{ disabled: boolean }>`
  ${IcChevron}

  ${(props) =>
    props.disabled &&
    css`
      cursor: auto;
      opacity: 0.45;
      pointer-events: none;
    `}
`;

const IcChevronRight = styled(ChevronRight)<{ disabled: boolean }>`
  ${IcChevron}

  ${(props) =>
    props.disabled &&
    css`
      cursor: auto;
      opacity: 0.45;
      pointer-events: none;
    `}
`;

function Pagination(props: PaginationProps) {
  const { currentPage, totalItems, onChangePage } = props;
  const hasPagination = totalItems > LIMIT_PRODUCT_PER_PAGE;
  const [numbers, setNumbers] = useState<number[]>([]);

  const totalPages = useMemo(() => {
    return totalItems ? Math.ceil(totalItems / LIMIT_PRODUCT_PER_PAGE) : 0;
  }, [totalItems]);

  useEffect(() => {
    let from: number;
    let to: number;

    if (totalPages <= 3) {
      from = 1;
      to = totalPages;
    } else if (currentPage === 1) {
      from = currentPage;
      to = 3;
    } else if (currentPage === totalPages) {
      from = totalPages - 2;
      to = totalPages;
    } else {
      from = currentPage - 1;
      to = currentPage + 1;
    }

    const numbersTemp = [];
    for (let i = from; i <= to; i += 1) {
      numbersTemp.push(i);
    }

    setNumbers(numbersTemp);
  }, [currentPage, totalPages]);

  const handlePreviousPage = () => {
    onChangePage(currentPage - 1);
  };

  const handleNextPage = () => {
    onChangePage(currentPage + 1);
  };

  const handleGoToPage = (page: number) => {
    onChangePage(page);
  };

  return hasPagination ? (
    <Container>
      {totalPages > 3 && <IcChevronLeft disabled={currentPage === 1} onClick={handlePreviousPage} />}
      {numbers.map((number) => (
        <PaginationNumber key={number} selected={currentPage === number} onClick={() => handleGoToPage(number)}>
          {number}
        </PaginationNumber>
      ))}
      {totalPages > 3 && <IcChevronRight disabled={currentPage === totalPages} onClick={handleNextPage} />}
    </Container>
  ) : null;
}

export default Pagination;
